import {
  usePartialSubscription,
  useShippingAddress,
  useSettings,
  useBenefits,
  useBillingInfo
} from '../stateData/hooks'
import { isStateEmpty } from '../lib/stateHelpers'
import {
  PartialSubscription,
  ShippingAddress,
  Settings,
  Benefits,
  BillingInfo,
  StateData,
  ReplacementEligibility
} from '../types'

export interface FlipFlowData {
  // Partial Subscription Data
  subscriptionId: number | null
  freeTrialMonths: number
  isPreEnrolled: boolean
  replacementEligibility: ReplacementEligibility | undefined
  isE2E: boolean
  appName: string
  requireBilling: boolean

  // Shipping Data
  shippingStreet1: string
  hasShipping: boolean

  // Settings Data
  settings: Partial<Settings>

  // Benefits Data
  benefits: Benefits | undefined

  // Billing Data
  hasBilling: boolean
  billingInfo: BillingInfo | undefined

  // Loading States
  isLoading: boolean
  isEmpty: boolean

  // Individual States (for granular access)
  states: {
    partialSubscription: StateData<PartialSubscription>
    shippingAddress: StateData<ShippingAddress>
    settings: StateData<Settings>
    benefits: StateData<Benefits>
    billingInfo: StateData<BillingInfo>
  }
}

/**
 * Composite hook that aggregates all API state data used in Flip flow components.
 * Provides a single source of truth for component data requirements, replacing
 * the pattern of importing 3-5 hooks individually in every component.
 *
 * @returns {FlipFlowData} Aggregated data with loading states
 *
 * @example
 * ```typescript
 * const {
 *   subscriptionId,
 *   hasShipping,
 *   settings,
 *   isLoading
 * } = useFlipFlowData()
 *
 * if (isLoading) return <LoadingPage />
 * ```
 */
export const useFlipFlowData = (): FlipFlowData => {
  const partialSubscription = usePartialSubscription()
  const shippingAddress = useShippingAddress()
  const settings = useSettings()
  const benefits = useBenefits()
  const billingInfo = useBillingInfo()

  const states = {
    partialSubscription,
    shippingAddress,
    settings,
    benefits,
    billingInfo
  }

  const isEmpty = isStateEmpty([partialSubscription, shippingAddress, settings])

  // Extract partial subscription data
  const psData = partialSubscription.data
  const subscriptionId = psData?.id ?? null
  const freeTrialMonths = psData?.freeTrialMonths ?? 0
  const isPreEnrolled = psData?.isPreEnrolled ?? false
  const replacementEligibility = psData?.replacementEligibility
  const isE2E = psData?.isE2E ?? false
  const appName = psData?.appName ?? ''
  const requireBilling = psData?.requireBilling ?? true

  // Extract shipping data
  const shippingStreet1 = shippingAddress.data?.street1 ?? ''
  const hasShipping = !!shippingStreet1

  // Extract settings data
  const settingsData: Partial<Settings> = settings.data ?? {}

  // Extract benefits data
  const benefitsData = benefits.data

  // Extract billing data
  const billingData = billingInfo.data
  const hasBilling = !!billingData?.paymentType

  const isLoading = isEmpty

  return {
    subscriptionId,
    freeTrialMonths,
    isPreEnrolled,
    replacementEligibility,
    isE2E,
    appName,
    requireBilling,

    shippingStreet1,
    hasShipping,

    settings: settingsData,

    benefits: benefitsData,

    hasBilling,
    billingInfo: billingData,

    isLoading,
    isEmpty,
    states
  }
}
